import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Menu,
  MenuItem as MuiMenuItem,
  Tooltip
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { MenuItem } from './menuConfig';
import { useStrings } from 'shared/utils/stringService';

interface MenuItemProps {
  item: MenuItem;
  level?: number;
  isExpanded: boolean;
  onToggle: (title: string) => void;
  onNavigate: (path: string) => void;
  hasRole: (role: any) => boolean;
  collapsed?: boolean;
}

export const SidebarMenuItem: React.FC<MenuItemProps> = ({
  item,
  level = 0,
  isExpanded,
  onToggle,
  onNavigate,
  hasRole,
  collapsed = false
}) => {
  const location = useLocation();
  const strings = useStrings();
  const hasChildren = item.children && item.children.length > 0;

  // Anchor for the children popover. When an item has children, clicking
  // the row opens a Menu anchored to the row rather than expanding inline
  // (which used to be a Collapse). The popover keeps the children
  // discoverable but gets them out of the sidebar's vertical real-estate.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  // Use stringKey lookup if available, but fall back to title if lookup returns 'undefined'
  const stringKeyValue = item.stringKey ? strings.get(item.stringKey as any) : null;
  const displayTitle = (stringKeyValue && stringKeyValue !== 'undefined') ? stringKeyValue : item.title;

  const isActive = item.path ? (
    level > 0
      ? location.pathname === item.path
      : (() => {
        // 1. Exact match on the item's own path.
        if (location.pathname === item.path) return true;
        // 2. Match on declared parentRoutes — both root-level (/library) and the
        //    company-scoped variant (/companies/:id/library) count, since platform
        //    users browse the same surfaces under both URL spaces.
        const matchesParentRoute = (p: string) => {
          if (location.pathname === p) return true;
          if (location.pathname.startsWith(p + '/')) return true;
          // /companies/:id<p> — e.g. /companies/abc/library matches parentRoute '/library'.
          const companyScoped = location.pathname.match(/^\/companies\/[^/]+(\/.*)?$/);
          if (companyScoped) {
            const sub = companyScoped[1] || '';
            if (sub === p || sub.startsWith(p + '/')) return true;
          }
          return false;
        };
        if (item.parentRoutes?.some(matchesParentRoute)) {
          return true;
        }
        // 3. Legacy behavior — keep the section-prefix matches that pre-date parentRoutes.
        if (item.path === '/library') {
          return location.pathname.startsWith('/library') || location.pathname.startsWith('/service-cards') || location.pathname.startsWith('/evaluations') || location.pathname.startsWith('/permits');
        }
        if (item.path === '/admins') {
          return location.pathname.startsWith('/admins');
        }
        if (item.path === '/safety_staff') {
          return location.pathname.startsWith('/safety_staff');
        }
        if (item.path === '/users') {
          return location.pathname.startsWith('/users');
        }
        return false;
      })()
  ) : false;

  const isChildActive = hasChildren && item.children!.some(
    child => child.path && location.pathname.startsWith(child.path)
  );

  const isParentOfActive = hasChildren && item.children!.some(
    child => child.path && location.pathname.startsWith(child.path)
  );

  const filteredChildren = hasChildren
    ? item.children!.filter(child => child.roles.some(role => hasRole(role)))
    : [];

  if (hasChildren && filteredChildren.length === 0) {
    return null;
  }

  if (collapsed && level > 0) {
    return null;
  }

  // Auto-navigate on click only when there's exactly one reachable child —
  // with 2+ (e.g. Usuarios > Internos/Externos), collapsing to the first
  // one permanently hid the rest, since clicking never fell through to
  // the popover-menu branch below. Multiple children still open the
  // popover so every one of them stays reachable while collapsed.
  const getFirstChildPath = (): string | null => {
    if (!hasChildren || !collapsed) return null;
    if (filteredChildren.length !== 1) return null;

    const firstAccessibleChild = filteredChildren.find(child => child.path);
    return firstAccessibleChild?.path || null;
  };

  const firstChildPath = getFirstChildPath();

  const isItemActive = isActive || (collapsed && isChildActive);
  const CORNER_R = 14;

  const button = (
    <ListItem disablePadding sx={{
      pl: collapsed ? 0.5 : 1.5,
      pr: 0,
      mb: level > 0 ? 0.15 : 0.3,
      position: 'relative',
      zIndex: isItemActive ? 2 : 1,
    }}>
      <ListItemButton
        data-testid={`sidebar-item-${item.id}`}
        onClick={(event) => {
          if (collapsed && firstChildPath) {
            onNavigate(firstChildPath);
          } else if (hasChildren) {
            setMenuAnchor(event.currentTarget as HTMLElement);
          } else if (item.path) {
            onNavigate(item.path);
          }
        }}
        sx={{
          pl: collapsed ? 1.25 : 2 + level * 2,
          pr: collapsed ? 1.25 : 1.5,
          py: 1.6,
          borderRadius: `${CORNER_R}px 0 0 ${CORNER_R}px`,
          minHeight: 52,
          minWidth: collapsed ? 48 : 'auto',
          justifyContent: collapsed ? 'center' : 'flex-start',
          position: 'relative',
          overflow: 'visible',
          backgroundColor: isItemActive
            ? (theme) => theme.palette.background.default
            : 'transparent',
          '&:hover': isItemActive
            ? {
                // Selected row already has a white plate + inverted corner
                // cutouts. Don't replace it with the translucent-white
                // overlay used for unselected rows — that overlay sits on
                // top of the dark sidebar and would visually erase the
                // selected plate (background, text, and icon colors all
                // get clobbered). Keep the active appearance as-is.
                backgroundColor: (theme) => theme.palette.background.default,
              }
            : {
                // Translucent white overlay reads as a lighter shade of the
                // sidebar blue. Plus restore label/icon/chevron to full
                // opacity so the row visibly responds to hover.
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                '& .MuiTypography-root': {
                  opacity: 1,
                  color: '#fff',
                },
                '& .sidebar-icon-badge': {
                  backgroundColor: 'rgba(255, 255, 255, 0.18)',
                  color: '#fff',
                },
                '& .sidebar-chevron': {
                  opacity: 1,
                  color: '#fff',
                },
          },
          // Inverted top-right corner
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: '100%',
            right: 0,
            width: CORNER_R,
            height: CORNER_R,
            pointerEvents: 'none',
            opacity: isItemActive ? 1 : 0,
            transition: 'opacity 0.3s ease',
            background: (theme) =>
              `radial-gradient(circle at 0 0, transparent ${CORNER_R}px, ${theme.palette.background.default} ${CORNER_R}px)`,
          },
          // Inverted bottom-right corner
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            right: 0,
            width: CORNER_R,
            height: CORNER_R,
            pointerEvents: 'none',
            opacity: isItemActive ? 1 : 0,
            transition: 'opacity 0.3s ease',
            background: (theme) =>
              `radial-gradient(circle at 0 100%, transparent ${CORNER_R}px, ${theme.palette.background.default} ${CORNER_R}px)`,
          },
          // The label color is applied directly via `primaryTypographyProps`
          // below — see the ListItemText element. Putting it here too led to
          // MUI's defaults winning specificity in some builds.
          transition: 'background-color 0.15s ease',
        }}
      >
        {item.icon && (
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 42,
              justifyContent: 'center',
            }}
          >
            <Box className="sidebar-icon-badge" sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (isItemActive || (isParentOfActive && isExpanded))
                ? (theme) => `${theme.palette.background.sidebarSelectedText}18`
                : (theme) => `${theme.palette.background.sidebartext}15`,
              color: (isItemActive || (isParentOfActive && isExpanded))
                ? (theme) => theme.palette.background.sidebarSelectedText
                : (theme) => theme.palette.background.sidebartext,
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '& svg': {
                fontSize: 20,
              },
            }}>
              {item.icon}
            </Box>
          </ListItemIcon>
        )}
        {!collapsed && (
          <>
            <ListItemText
              primary={displayTitle}
              sx={{ ml: 0 }}
              primaryTypographyProps={{
                // Apply the color directly to the Typography so MUI's default
                // `rgba(0, 0, 0, 0.54)` on disabled/secondary states can't leak
                // through.
                sx: {
                  color: (theme) =>
                    isItemActive || (isParentOfActive && isExpanded)
                      ? theme.palette.background.sidebarSelectedText
                      : theme.palette.background.sidebartext,
                  opacity: isItemActive || (isParentOfActive && isExpanded) ? 1 : 0.7,
                  fontWeight: level > 0 ? 400 : 500,
                  fontSize: level > 0 ? '0.8rem' : '0.85rem',
                  transition: 'color 0.3s ease, opacity 0.3s ease',
                },
              }}
            />
            {hasChildren && (
              <Box className="sidebar-chevron" sx={{
                color: (menuOpen || isParentOfActive)
                  ? (theme) => theme.palette.background.sidebarSelectedText
                  : (theme) => theme.palette.background.sidebartext,
                '& svg': { fontSize: 14 },
                opacity: 0.7,
                transition: 'color 0.15s ease, opacity 0.15s ease',
              }}>
                {menuOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </>
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      {collapsed && level === 0 ? (
        <Tooltip
          title={hasChildren && firstChildPath ? `${displayTitle} (ver lista)` : displayTitle}
          placement="right"
          arrow
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}

      {hasChildren && (
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={closeMenu}
          // Anchor at the row's right edge but pull the popover's right edge
          // back over the row (negative ml) so the popover overlaps into the
          // sidebar item rather than floating off to the side. The visual
          // intent is "the row expanded a panel inside itself," not "a
          // floating flyout menu".
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              elevation: 6,
              sx: {
                // Pull the popover left a bit so it overlaps into the
                // sidebar row's right portion. ml is negative pixels.
                mt: -0.5,
                ml: 6,
                minWidth: 180,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
              },
            },
          }}
        >
          {filteredChildren.map((child) => {
            const childActive = !!child.path && location.pathname + (location.search || '') === child.path;
            return (
              <MuiMenuItem
                key={child.title}
                onClick={() => {
                  if (child.path) onNavigate(child.path);
                  closeMenu();
                }}
                sx={{
                  py: 1.25,
                  px: 2,
                  // Adaptive tokens so the submenu popover works in dark mode
                  // too (previously hardcoded navy-on-white).
                  color: 'text.primary',
                  fontWeight: childActive ? 600 : 500,
                  fontSize: '0.875rem',
                  backgroundColor: childActive ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {/* Custom stringKey lookup is unnecessary for direct children of
                    a parent that's already showing its translated title. */}
                {child.title}
              </MuiMenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};